/**
 * @author      sammy.dowds@zumper.com (Sammy Dowds)
 * @copyright   Copyright (c) 2023, Zumper
 */

import { type LinkOrButtonProps, VerifiedIcon } from '@zcode-ui/web-zumper-ui'
import { a11yLinkHandler } from '@zcode/a11y-utils'
import type { splitOnMiddleDot } from '@zcode/nr-utils'
import type { Listable, Pin, RawPinInventorySearch } from '@zcode/state-type'
import cn from 'classnames'
import React, { Fragment, type KeyboardEvent, type MouseEvent } from 'react'
import css from './ListingCardContentSection.module.scss'
import { getDetailLinkText } from './helpers/getDetailLinkText'

export interface GetCommonEntityDataInput {
  entity: Listable | Pin<RawPinInventorySearch>
  isShortTerm?: boolean
  showShortPrice?: boolean
}
export interface GetCommonEntityDataOutput {
  bathsRange: ReplaceAll<
    ReturnType<
      typeof splitOnMiddleDot<(Listable | Pin<RawPinInventorySearch>)['bedsBathsRangeTxt']>
    >[1],
    ' - ',
    '\u2013'
  >
  bedsRange: ReplaceAll<
    ReturnType<
      typeof splitOnMiddleDot<(Listable | Pin<RawPinInventorySearch>)['bedsBathsRangeTxt']>
    >[0],
    ' - ',
    '\u2013'
  >
  cityAndHood: string
  fullAddress: string
  previousPriceText: string | undefined
  price: 'Call for pricing' | 'Call for price' | Listable['priceText' | 'priceTxt']
  shortTermHeaderText: string
  shortTermPrice: string
  shortTermPropertyText: string
  showPricePerNight: boolean | undefined
}

export interface DetailPageLinkProps {
  children: React.ReactNode
  className?: string
  entity: Listable | Pin<RawPinInventorySearch>
  onClick?: LinkOrButtonProps['onClick']
  tabIndex?: number
}

export interface ListingCardContentSectionProps {
  DetailPageLink: React.ComponentType<DetailPageLinkProps>
  entity: Listable | Pin<RawPinInventorySearch>
  forceMobile?: boolean
  forceDesktop?: boolean
  getCommonEntityData: (input: GetCommonEntityDataInput) => GetCommonEntityDataOutput
  getHoodPropertyText: (entity: Listable | Pin<RawPinInventorySearch>) => string
  handleQuickLookClick: (e: MouseEvent<HTMLButtonElement>) => void
  hideQuickLook?: boolean
  isLargeCard: boolean
  isShortTerm?: boolean
  onDetailClick?: (e: KeyboardEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>) => void
  ListCardRating: React.ComponentType<{
    gapStyle?: string
    rating?: number | null
    ratingStyle?: string
    textStyle?: string
  }>
  showInfo: boolean
  updatedAmenities?: ReadonlyArray<string>
}

export const ListingCardContentSection = ({
  DetailPageLink,
  entity,
  forceDesktop,
  forceMobile,
  getCommonEntityData,
  getHoodPropertyText,
  handleQuickLookClick,
  hideQuickLook,
  isLargeCard,
  isShortTerm,
  ListCardRating,
  onDetailClick,
  showInfo,
  updatedAmenities,
}: ListingCardContentSectionProps) => {
  const { features, isBuilding, isVerified, maxAdults, maxPrice, minPrice, previousPrice, rating } =
    entity
  const showPriceDropBuilding = features?.includes(77) && isBuilding

  const showPriceDropAddress =
    features?.includes(77) &&
    maxPrice === minPrice &&
    previousPrice &&
    maxPrice &&
    previousPrice > maxPrice &&
    !isBuilding
  const {
    bedsRange,
    bathsRange,
    fullAddress,
    price,
    previousPriceText,
    shortTermHeaderText,
    shortTermPrice,
    shortTermPropertyText,
    showPricePerNight,
  } = getCommonEntityData({ entity, isShortTerm })
  const amenities = updatedAmenities ?? entity.combinedAmenities
  const slicedAmenities = amenities.slice(0, 3)
  const showRating = !!(rating && isBuilding && !isShortTerm) || !!(rating && isShortTerm)
  const showQuickLook = isLargeCard && !hideQuickLook
  const detailLinkText = getDetailLinkText(entity, fullAddress)
  const addressSubheader = !detailLinkText.includes(fullAddress) ? fullAddress : ''

  return (
    <div className={cn(css.contentContainer, 'ListingCardContentSection')}>
      <div
        className={cn(
          css.content,
          forceMobile && css.contentMobile,
          forceDesktop && css.contentDesktop
        )}
      >
        <div className={css.header}>
          {showRating ? (
            <ListCardRating
              gapStyle={css.ratingGapStyle}
              rating={rating}
              ratingStyle={css.ratingBoxStyle}
              textStyle={css.ratingTextStyle}
            />
          ) : null}

          {isVerified && !isShortTerm && (
            <Fragment>
              {showRating && <div className={css.ratingDivider} />}
              <div className={css.verifiedContainer}>
                <VerifiedIcon
                  popoverId={`verified-${entity.uId}`}
                  isDirectDeal
                  isNewDetailPage
                  size={18}
                />
                <p className={css.verifiedText}>Verified</p>
              </div>
            </Fragment>
          )}
          {isShortTerm && (
            <Fragment>
              {showRating && <div className={cn(css.ratingDivider, css.shortTermRatingDivider)} />}
              <button
                className={css.detailButton}
                onClick={onDetailClick}
                onKeyPress={onDetailClick && a11yLinkHandler(onDetailClick)}
                type="button"
              >
                Details
              </button>
            </Fragment>
          )}
          {!showRating && !isVerified && !isShortTerm && (
            <p className={css.hoodPropertyText}>{getHoodPropertyText(entity)}</p>
          )}
        </div>

        {showQuickLook && (
          <button
            className={cn(
              css.quickLookButton,
              forceMobile && css.quickLookButtonMobile,
              forceDesktop && css.quickLookButtonDesktop
            )}
            onClick={handleQuickLookClick}
            type="button"
          >
            Quick look
          </button>
        )}
      </div>
      <div>
        <div
          className={cn(
            css.listingInfo,
            isLargeCard && css.listingInfoLargeCard,
            isLargeCard && forceDesktop && css.listingInfoLargeCardDesktop,
            isLargeCard && forceMobile && css.listingInfoLargeCardMobile
          )}
        >
          {isShortTerm && (
            <p
              className={cn(
                css.shortTermHeaderText,
                isLargeCard && css.shortTermHeaderTextLargeCard
              )}
            >
              {shortTermHeaderText}
            </p>
          )}
          {!isShortTerm ? (
            <DetailPageLink
              aria-hidden={showInfo}
              className={`font-bold font-heading hover:no-underline overflow-hidden text-ellipsis text-gray-950 text-left ${isLargeCard ? 'text-[18px]' : 'text-sm'}`}
              entity={entity}
              tabIndex={showInfo ? -1 : 0}
            >
              <div className={css.detailLinkText}>{detailLinkText}</div>
            </DetailPageLink>
          ) : null}
          <div className={css.addressContainer}>
            {isShortTerm && !isLargeCard && (
              <ListCardRating
                gapStyle={css.ratingGapStyleShortTerm}
                rating={rating}
                ratingStyle={css.ratingBoxStyleShortTerm}
                textStyle={css.ratingTextStyleShortTerm}
              />
            )}
            <p
              className={cn(
                'ListingCardContentSection-fullAddress-shortTermPropertyText',
                css.addressText,
                isLargeCard && css.addressTextLargeCard,
                isLargeCard && forceMobile && css.addressTextLargeCardMobile,
                isLargeCard && forceDesktop && css.addressTextLargeCardDesktop
              )}
            >
              {isShortTerm ? shortTermPropertyText : addressSubheader}
            </p>
          </div>
          {isLargeCard && (
            <div
              className={cn(
                css.amenitiesContainer,
                forceMobile && css.amenitiesContainerMobile,
                forceDesktop && css.amenitiesContainerDesktop
              )}
            >
              <p
                className={cn(
                  css.amenitiesText,
                  forceMobile && css.amenitiesTextMobile,
                  forceDesktop && css.amenitiesTextDesktop
                )}
              >
                {slicedAmenities.map((amenity, i) => {
                  const isLast = i === slicedAmenities.length - 1
                  return (
                    <Fragment key={amenity}>
                      {amenity}
                      {!isLast ? <span className={css.amenitiesDivider}> | </span> : null}
                    </Fragment>
                  )
                })}
              </p>
            </div>
          )}
        </div>
        <div
          className={cn(
            css.bedBathPriceContainer,
            forceDesktop && css.bedBathPriceContainerDesktop,
            isLargeCard && css.bedBathPriceContainerLargeCard,
            isLargeCard && forceDesktop && css.bedBathPriceContainerLargeCardDesktop,
            forceMobile && css.bedBathPriceContainerMobile
          )}
        >
          <div className={css.bedBathContainer}>
            <p
              className={cn(
                css.bedsRangeText,
                isLargeCard && css.bedsRangeTextLargeCard,
                isLargeCard && forceMobile && css.bedsRangeTextLargeCardMobile,
                isLargeCard && forceDesktop && css.bedsRangeTextLargeCardDesktop
              )}
            >
              {bedsRange}
            </p>
            <div className={css.bedBathDivider} />
            <p
              className={cn(
                css.bedsRangeText,
                !isShortTerm && css.bathRangeTextLongTerm,
                isLargeCard && css.bedsRangeTextLargeCard,
                isLargeCard && forceMobile && css.bedsRangeTextLargeCardMobile,
                isLargeCard && forceDesktop && css.bedsRangeTextLargeCardDesktop
              )}
            >
              {bathsRange}
            </p>
            {isShortTerm && maxAdults && (
              <Fragment>
                <div className={css.bedBathDivider} />
                <p
                  className={cn(
                    css.bedsRangeText,
                    css.bathRangeTextLongTerm,
                    isLargeCard && css.bedsRangeTextLargeCard,
                    isLargeCard && forceMobile && css.bedsRangeTextLargeCardMobile,
                    isLargeCard && forceDesktop && css.bedsRangeTextLargeCardDesktop
                  )}
                >
                  Sleeps {maxAdults}
                </p>
              </Fragment>
            )}
          </div>
          <div className={css.priceContainer}>
            {showPriceDropBuilding && !showPriceDropAddress && (
              <>
                <p className={cn(css.priceDropText, isLargeCard && css.priceDropTextLargeCard)}>
                  {isShortTerm ? shortTermPrice : price}
                </p>

                <p className={css.priceDrop}>Price drop</p>
              </>
            )}
            {showPriceDropAddress && (
              <>
                <p className={cn(css.previousPrice, isLargeCard && css.priceDropTextLargeCard)}>
                  {previousPriceText}
                </p>
                <p className={cn(css.priceDropText, isLargeCard && css.priceDropTextLargeCard)}>
                  {isShortTerm ? shortTermPrice : price}
                </p>
                <p className={css.priceDrop}>Price drop</p>
              </>
            )}
            {!showPriceDropBuilding && !showPriceDropAddress && (
              <p className={cn(css.longTermPrice, isLargeCard && css.longTermPriceLargeCard)}>
                {isShortTerm ? shortTermPrice : price}
              </p>
            )}
            {isShortTerm && !showPriceDropBuilding && !showPriceDropAddress && (
              <p className={css.shortTermPriceText}>{showPricePerNight ? '/night' : '/month'}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
